import { useState } from "react";
import { Send, CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { COMPANY } from "../lib/site";

const SERVICE_OPTIONS = ["Manned Guarding", "CCTV & Surveillance", "K9 Patrols", "Security Systems", "Not sure yet"];

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "Manned Guarding", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent", { description: "A consultant will reach you within one working day." });
      setForm({ name: "", email: "", phone: "", company: "", service: "Manned Guarding", message: "" });
    } catch (err) {
      toast.error("Couldn't send", { description: "Please try again or call us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="contact-section" id="contact" className="relative py-24 lg:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">
              ◆ Get In Touch
            </p>
            <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl leading-[1.05] tracking-tighter mb-5">
              Speak to a UK consultant<br />
              <span className="text-gradient-cyan">today.</span>
            </h2>
            <p className="text-slate-400 leading-relaxed mb-8">
              Tell us about your site and we'll build a tailored, no-obligation proposal. Most clients hear back within a working day — urgent cases are escalated immediately.
            </p>

            <div className="space-y-4">
              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center group-hover:bg-cyan-500/20 transition shrink-0">
                  <Mail className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Email</div>
                  <div className="text-white font-medium">{COMPANY.email}</div>
                </div>
              </a>

              {COMPANY.offices.map((office) => (
                <div key={office.id} data-testid={`office-${office.id}`} className="card-tech rounded-lg p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mb-1">{office.name}</div>
                      <div className="text-white text-sm leading-relaxed">
                        {office.address_lines.map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                      </div>
                      <a
                        href={`tel:${office.phone_href}`}
                        data-testid={`office-phone-${office.id}`}
                        className="inline-flex items-center gap-1.5 mt-2 text-cyan-300 hover:text-cyan-200 font-mono text-sm transition"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        {office.phone}
                      </a>
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex items-center gap-4 pt-1">
                <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Coverage</div>
                  <div className="text-white font-medium">{COMPANY.address}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form data-testid="contact-form" onSubmit={submit} className="card-tech rounded-2xl p-7 lg:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" name="name" value={form.name} onChange={(v)=>setForm({...form, name:v})} testid="contact-name" required />
                <Field label="Work email" name="email" type="email" value={form.email} onChange={(v)=>setForm({...form, email:v})} testid="contact-email" required />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Phone" name="phone" value={form.phone} onChange={(v)=>setForm({...form, phone:v})} testid="contact-phone" />
                <Field label="Company" name="company" value={form.company} onChange={(v)=>setForm({...form, company:v})} testid="contact-company" />
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">Service of interest</label>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_OPTIONS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      data-testid={`service-tag-${s}`}
                      onClick={() => setForm({ ...form, service: s })}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition border ${
                        form.service === s
                          ? "bg-cyan-500/15 border-cyan-400/50 text-cyan-200"
                          : "bg-white/5 border-white/10 text-slate-400 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">Tell us about your site</label>
                <textarea
                  data-testid="contact-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e)=>setForm({...form, message: e.target.value})}
                  placeholder="Site location, size, current security setup, urgency..."
                  className="w-full bg-ink-900 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/50 transition resize-none"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-slate-500">We treat your data confidentially. ICO registered.</p>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-ink-950 font-bold rounded-md px-6 py-3 text-sm transition"
                >
                  {done ? (<><CheckCircle2 className="w-4 h-4" /> Sent</>) : submitting ? "Sending..." : (<>Send message <Send className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, type = "text", value, onChange, required, testid }) => (
  <div>
    <label className="block text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">{label}{required ? " *" : ""}</label>
    <input
      data-testid={testid}
      required={required}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-ink-900 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/50 transition"
    />
  </div>
);
